import React, { useState, useEffect, useCallback } from 'react';
import { isSuperAdmin } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL || '';

export default function ContactMessagesPage({ user }) {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const limit = 25;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canDelete = isSuperAdmin(user);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filterStatus) params.append('status', filterStatus);
      if (search) params.append('search', search);

      const res = await fetch(`${API}/api/admin/contact-messages?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch contact messages:', err);
    }
    setLoading(false);
  }, [page, filterStatus, search]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleStatusChange = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/contact-messages/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({ status })
      });
      fetchMessages();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this message?')) return;
    try {
      await fetch(`${API}/api/admin/contact-messages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      fetchMessages();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const statusColors = {
    new: { background: '#EEF2FF', color: '#4F46E5' },
    read: { background: '#F9FAFB', color: '#6B7280' },
    replied: { background: '#F0FDF4', color: '#16A34A' },
    archived: { background: '#F3F4F6', color: '#9CA3AF' },
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Messages</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{total} message{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search name, email, or subject..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={inputStyle}
        />
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Subject</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : messages.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No messages found</td></tr>
            ) : (
              messages.map(msg => (
                <tr key={msg.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={tdStyle}>{msg.name}</td>
                  <td style={tdStyle}><a href={`mailto:${msg.email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>{msg.email}</a></td>
                  <td style={{ ...tdStyle, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={msg.message}>{msg.subject}</td>
                  <td style={tdStyle}>
                    <select
                      value={msg.status}
                      onChange={e => handleStatusChange(msg.id, e.target.value)}
                      style={{ ...badgeBase, ...statusColors[msg.status], border: 'none', cursor: 'pointer' }}
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td style={tdStyle}>{new Date(msg.created_at).toLocaleDateString('en-GB')}</td>
                  <td style={tdStyle}>
                    {canDelete && (
                      <button onClick={() => handleDelete(msg.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14 }}>
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>← Prev</button>
          <span style={{ padding: '8px 16px', fontSize: 14, color: '#6B7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Next →</button>
        </div>
      )}
    </div>
  );
}

const inputStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, minWidth: 220, background: '#F9FAFB' };
const selectStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, background: '#F9FAFB', cursor: 'pointer' };
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '14px 16px', fontSize: 14 };
const badgeBase = { padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' };
const pageBtnStyle = { padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 };
